import { motion } from "framer-motion";
import { FinalStats } from "./RainEndPhase";
import { Gift, Users } from "lucide-react";

const EspontIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <img src="/espont.png" alt="" className={`${className} object-contain`} />
);

interface RewardScreenProps {
  finalStats: FinalStats;
}

export function RewardScreen({ finalStats }: RewardScreenProps) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center p-5">
      <motion.div
        initial={{ opacity: 0, y: -18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="mb-3 flex items-center gap-2 text-center"
      >
        <p className="text-xl font-black uppercase text-yellow-200 drop-shadow-[0_0_14px_rgba(253,224,71,0.45)]">
          FINAL RESULTS
        </p>
      </motion.div>

      <div className="w-full max-w-md">
        <motion.div
          className="relative overflow-hidden rounded-2xl border border-yellow-300/50 bg-yellow-950/35 px-5 py-5 text-center shadow-[0_0_34px_rgba(253,224,71,0.18)]"
          initial={{ opacity: 0, scale: 0.9, y: 22 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "backOut" }}
        >
          <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-yellow-200 to-transparent" />
          <div className="mb-2 flex items-center justify-center gap-2 text-xs font-black uppercase text-yellow-100/85">
            <EspontIcon className="h-5 w-5 drop-shadow-[0_0_8px_rgba(253,224,71,0.75)]" />
            <span>Total Pot</span>
          </div>
          <motion.div
            className="flex items-center justify-center gap-3 text-yellow-300"
            animate={{ scale: [1, 1.035, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
          >
            <p className="text-5xl leading-none font-black drop-shadow-[0_0_18px_rgba(253,224,71,0.58)]">
              {finalStats.totalPot.toLocaleString()}
            </p>
            <EspontIcon className="h-8 w-8 drop-shadow-[0_0_10px_rgba(253,224,71,0.75)]" />
          </motion.div>
        </motion.div>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <motion.div
            className="flex min-h-[70px] items-center justify-between gap-3 rounded-xl border border-emerald-300/45 bg-emerald-950/45 px-4 py-3 shadow-[0_0_22px_rgba(52,211,153,0.14)]"
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.18, duration: 0.45 }}
          >
            <div className="min-w-0">
              <p className="text-xs font-black uppercase text-emerald-100/80">
                Participants
              </p>
              <p className="mt-1 text-3xl leading-none font-black text-emerald-300">
                {finalStats.participantCount}
              </p>
            </div>
            <Users className="h-7 w-7 flex-shrink-0 text-emerald-200" />
          </motion.div>

          <motion.div
            className="flex min-h-[70px] items-center justify-between gap-3 rounded-xl border border-sky-300/45 bg-sky-950/45 px-4 py-3 shadow-[0_0_22px_rgba(56,189,248,0.14)]"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.28, duration: 0.45 }}
          >
            <div className="min-w-0">
              <p className="text-xs font-black uppercase text-sky-100/80">
                Each
              </p>
              <div className="mt-1 flex items-center gap-2 text-sky-300">
                <p className="text-3xl leading-none font-black">
                  {finalStats.awardPerPerson.toFixed(0)}
                </p>
                <EspontIcon className="h-5 w-5 flex-shrink-0 drop-shadow-[0_0_8px_rgba(253,224,71,0.75)]" />
              </div>
            </div>
            <Gift className="h-7 w-7 flex-shrink-0 text-sky-200" />
          </motion.div>
        </div>
      </div>
    </div>
  );
}
